import { getAutoCompleteSuggestions } from 'shared/utils/get-auto-complete-suggestions';

import { Option } from './types';

export const flattenOptions = (options: Option[]): Option[] => {
  if (!options.length) {
    return [];
  }

  return options.reduce<Option[]>((acc, option) => {
    acc.push(option);

    if (option.children?.length) {
      acc.push(...flattenOptions(option.children));
    }

    return acc;
  }, []);
};

export const getAllDescendantValues = (option: Option): string[] => {
  const values: string[] = option.selectChildrenOnly ? [] : [option.value];

  if (!option.children?.length) {
    return values;
  }

  option.children.forEach((child) => {
    values.push(...getAllDescendantValues(child));
  });

  return values;
};

export const findOptionByValue = (
  value: string,
  options: Option[],
): Option | undefined => {
  for (const option of options) {
    if (option.value === value) {
      return option;
    }

    if (option.children?.length) {
      const found = findOptionByValue(value, option.children);

      if (found) {
        return found;
      }
    }
  }

  return undefined;
};

export const getNextValuesAfterToggle = (
  option: Option,
  currentValues: string[] = [],
  recursiveSelection = false,
): string[] | undefined => {
  let descendantValues = getAllDescendantValues(option);

  if (option.selectChildrenOnly) {
    if (!descendantValues.length) {
      return currentValues.length ? currentValues : undefined;
    }

    const hasAllDescendantsSelected = descendantValues.every((descendant) =>
      currentValues.includes(descendant),
    );

    if (hasAllDescendantsSelected) {
      const nextValues = currentValues.filter(
        (currentValue) => !descendantValues.includes(currentValue),
      );

      return nextValues.length ? nextValues : undefined;
    }

    const nextValues = Array.from(
      new Set([...currentValues, ...descendantValues]),
    );

    return nextValues.length ? nextValues : undefined;
  }

  const isSelected = currentValues.includes(option.value);
  const filteredValues = currentValues.filter(
    (currentValue) => currentValue !== option.value,
  );

  if (!recursiveSelection || !option.children?.length) {
    if (!isSelected) {
      const nextValues = [...currentValues, option.value];

      return nextValues.length ? nextValues : undefined;
    }

    return filteredValues.length ? filteredValues : undefined;
  }

  descendantValues = getAllDescendantValues(option);

  if (isSelected) {
    const nextValues = currentValues.filter(
      (currentValue) => !descendantValues.includes(currentValue),
    );

    return nextValues.length ? nextValues : undefined;
  }

  const nextValues = Array.from(
    new Set([...currentValues, ...descendantValues]),
  );

  return nextValues.length ? nextValues : undefined;
};

export const filterOptionsTree = (
  options: Option[],
  searchQuery: string,
): Option[] => {
  const matchesAtThisLevel = getAutoCompleteSuggestions(searchQuery, options, [
    'label',
  ]);
  const matchingValues = new Set(
    matchesAtThisLevel.map((option) => option.value),
  );

  return options.reduce<Option[]>((acc, option) => {
    let filteredChildren: Option[] = [];
    if (option.children?.length) {
      filteredChildren = filterOptionsTree(option.children, searchQuery);
    }

    if (matchingValues.has(option.value) || filteredChildren.length) {
      acc.push({
        ...option,
        children: filteredChildren.length ? filteredChildren : undefined,
      });
    }

    return acc;
  }, []);
};
